import React, { useEffect, useRef, useState } from "react";
import { IoAdd, IoRemove } from "react-icons/io5";
import { RockerKey, RoundKey } from "./RemoteKeys";
import { AC_TEMPS, KEYS, tempKey } from "./layouts";

const MIN_TEMP = AC_TEMPS[0];
const MAX_TEMP = AC_TEMPS[AC_TEMPS.length - 1];
const storageKey = (remoteId) => `hx-remote-ac-temp:${remoteId}`;

// The app remembers the last temperature it set (app/providers/RemoteProvider.js)
// for its display and the ON key. Here it's remembered per remote.
const readTemp = (remoteId) => {
  try {
    const degrees = Number(localStorage.getItem(storageKey(remoteId)));
    return AC_TEMPS.includes(degrees) ? degrees : null;
  } catch (error) {
    return null;
  }
};

// AC remote as in the app (app/screens/HashhRemote/Layouts/ACRemote.js): the set
// temperature, then ON, a Temp rocker and OFF.
function AcRemote({ remoteId, isLearned, onKey }) {
  const [temp, setTemp] = useState(() => readTemp(remoteId));
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!temp) return;
    try {
      localStorage.setItem(storageKey(remoteId), String(temp));
    } catch (error) {
      // Without storage the display starts empty next time.
    }
  }, [remoteId, temp]);

  const hasTemps = AC_TEMPS.some((degrees) => isLearned(tempKey(degrees)));
  // As in the app, the first step up sets 16 °C and the ends of the range repeat.
  const upTo = temp ? Math.min(temp + 1, MAX_TEMP) : MIN_TEMP;
  const downTo = temp ? Math.max(temp - 1, MIN_TEMP) : null;

  // The app follows ON with the set temperature half a second later.
  const powerOn = () => {
    onKey("ON");
    if (!temp) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onKey(tempKey(temp)), 500);
  };

  // The display moves even when that temperature isn't learned (onKey then
  // says so), so the user can step past gaps, as in the app.
  const step = (degrees) => {
    onKey(tempKey(degrees));
    setTemp(degrees);
  };

  return (
    <>
      <div className="hx-remote-ac-display">
        <p className="hx-remote-ac-temp" aria-live="polite">
          <span aria-hidden="true">
            <span className="hx-remote-ac-value">{temp ?? "_"}</span>°C
          </span>
          <span className="hx-remote-sr">{temp ? `Set to ${temp} °C` : "Temperature not set"}</span>
        </p>
      </div>
      <div className="hx-remote-row">
        <RoundKey
          big
          label={KEYS.ON.label}
          icon={KEYS.ON.icon}
          tone={KEYS.ON.tone}
          disabled={!isLearned("ON")}
          onPress={powerOn}
        />
        <RockerKey
          label="Temp"
          groupLabel="Temperature"
          up={{ label: "Temperature up", icon: IoAdd, disabled: !hasTemps, onPress: () => step(upTo) }}
          down={{ label: "Temperature down", icon: IoRemove, disabled: !hasTemps || !temp, onPress: () => step(downTo) }}
        />
        <RoundKey
          big
          label={KEYS.OFF.label}
          icon={KEYS.OFF.icon}
          tone={KEYS.OFF.tone}
          disabled={!isLearned("OFF")}
          onPress={() => onKey("OFF")}
        />
      </div>
    </>
  );
}

export default AcRemote;
